import { useTheme } from "@/hooks/useTheme";
import { Stack, router, useGlobalSearchParams, useSegments } from "expo-router";
import { Pressable, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialIcons } from "@expo/vector-icons";
import * as Haptics from "expo-haptics";
import useAuth from "@/hooks/useAuth";

function HomeBackNavigator({ title, context, toShift }: { title?: string; context?: string; toShift?: string }) {
  const { colors } = useTheme();
  const segs = useSegments();

  const handlePress = () => {
    if (Platform.OS !== "web") {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }

    const basePath = (segs as string[]).includes("crew") ? "/crew/home" : "/home";

    // If toShift is provided, navigate to shift detail with context
    if (toShift) {
      router.replace({
        pathname: `${basePath}/${toShift}` as any,
        params: { context },
      });
      return;
    }

    // Otherwise navigate to the correct tab based on context
    if (context === "accepted") {
      router.replace(`${basePath}/accepted` as any);
    } else {
      router.replace(basePath as any);
    }
  };

  return (
    <Pressable
      onPress={handlePress}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 4,
        opacity: pressed ? 0.7 : 1,
        minWidth: 60,
      })}
    >
      <MaterialIcons color={colors.primary} size={24} name="chevron-left" />
      {title && (
        <ThemedText
          style={{
            color: colors.primary,
            fontSize: 17,
            fontWeight: "400",
            marginLeft: 2,
          }}
        >
          {title}
        </ThemedText>
      )}
    </Pressable>
  );
}

export default function ShiftDetailsLayout() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const { context, shift } = useGlobalSearchParams<{ context?: string; shift?: string }>();
  const shiftName = displayNames?.shifts?.name || "Shift";

  const contextLabel = context === "accepted" ? "Accepted" : "Invited";
  const backLabel = context === "accepted" ? "Accepted" : "Invited";

  return (
    <Stack
      initialRouteName="index"
      screenOptions={{
        headerShadowVisible: false,
        contentStyle: {
          backgroundColor: colors.backgroundSecondary,
        },
        headerStyle: {
          backgroundColor: colors.backgroundSecondary,
        },
        headerTitle: `${contextLabel} ${shiftName}`,
        headerTitleAlign: "center",
        headerTitleStyle: {
          color: colors.text,
          fontWeight: "600",
          fontSize: 18,
        },
      }}
    >
      <Stack.Screen
        options={{
          animation: "simple_push",
          headerLeft: () => <HomeBackNavigator title={backLabel} context={context} />,
        }}
        name="index"
      />
      <Stack.Screen
        name="user/[user]"
        options={{
          animation: "simple_push",
          headerTitle: "Team Member",
          headerLeft: () => <HomeBackNavigator title={"Back"} context={context} toShift={shift} />,
        }}
      />
      <Stack.Screen
        name="supervisor/[supervisor]"
        options={{
          animation: "simple_push",
          headerTitle: "Supervisor",
          headerLeft: () => <HomeBackNavigator title={"Back"} context={context} toShift={shift} />,
        }}
      />
      <Stack.Screen
        name="contact"
        options={{
          animation: "simple_push",
          headerTitle: "Onsite Contact",
          headerLeft: () => <HomeBackNavigator title={"Back"} context={context} toShift={shift} />,
        }}
      />
      <Stack.Screen
        name="expense"
        options={{
          animation: "simple_push",
          headerTitle: "Expense",
          headerLeft: () => <HomeBackNavigator title={"Back"} context={context} toShift={shift} />,
        }}
      />
      <Stack.Screen
        name="incident"
        options={{
          animation: "simple_push",
          headerTitle: "Report Incident",
          headerLeft: () => <HomeBackNavigator title={"Back"} context={context} toShift={shift} />,
        }}
      />
    </Stack>
  );
}
